import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Music, Globe, DollarSign, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader 
} from '@/components/layout/MobileLayout';
import { mockArtists, mockEvents } from '@/lib/mockData';
import { formatDate } from '@/lib/utils';

export function UserArtistDetails() {
  const { artistId } = useParams();
  const artist = mockArtists.find(a => a.id === artistId) || mockArtists[0];
  
  // Find events this artist performed at
  const artistEvents = mockEvents.filter(e => 
    e.artists?.some(a => a.id === artistId)
  );

  // Find upcoming events (for "Next Performance" metric)
  const upcomingEvents = artistEvents.filter(e => e.status === 'upcoming').sort((a, b) => new Date(a.date) - new Date(b.date));
  const nextPerformance = upcomingEvents[0];
  const pastEvents = artistEvents.filter(e => e.status === 'past');

  return (
    <div className="min-h-screen bg-background" data-testid="user-artist-details">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header */}
      <DesktopHeader />
      
      <main className="pt-14 lg:pt-20 pb-24 lg:pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Home
          </Link>

          <Card className="bg-card border-border overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary/20 to-accent/20 p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary">
                  <img 
                    src={artist.image} 
                    alt={artist.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center md:text-left">
                  <h1 className="text-3xl font-bold text-white mb-2">{artist.name}</h1>
                  <p className="text-primary flex items-center justify-center md:justify-start gap-2">
                    <Music className="w-4 h-4" />
                    {artist.instrument}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Bio */}
              <div>
                <h2 className="text-lg font-semibold text-white mb-2">About</h2>
                <p className="text-muted-foreground">{artist.bio}</p>
              </div>

              {/* Stats - Including Next Performance */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-secondary/50 border-border p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{artist.totalPerformances}</p>
                  <p className="text-sm text-muted-foreground">Total Performances</p>
                </Card>
                <Card className="bg-secondary/50 border-border p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{artistEvents.length}</p>
                  <p className="text-sm text-muted-foreground">Events on EXIT88</p>
                </Card>
                <Card className="bg-secondary/50 border-border p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{formatDate(artist.lastEvent)}</p>
                  <p className="text-sm text-muted-foreground">Last Performance</p>
                </Card>
                {/* Next Performance Metric Box */}
                <Card className={`border-border p-4 text-center ${nextPerformance ? 'bg-primary/20 border-primary/50' : 'bg-secondary/50'}`}>
                  {nextPerformance ? (
                    <>
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Clock className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-lg font-bold text-primary">{formatDate(nextPerformance.date)}</p>
                      <p className="text-sm text-muted-foreground">Next Performance</p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-bold text-muted-foreground">—</p>
                      <p className="text-sm text-muted-foreground">Next Performance</p>
                    </>
                  )}
                </Card>
              </div>

              {/* Links */}
              <div className="space-y-4">
                {artist.website && (
                  <a href={artist.website} target="_blank" rel="noopener noreferrer" className="inline-block">
                    <Button variant="outline" className="border-border">
                      <Globe className="w-4 h-4 mr-2" />
                      Website
                    </Button>
                  </a>
                )}
                
                {/* Tipping Links Section */}
                {artist.tipLinks && (artist.tipLinks.paypal || artist.tipLinks.venmo || artist.tipLinks.cashApp || artist.tipLinks.customTip) && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-primary" />
                      Support This Artist
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {artist.tipLinks.paypal && (
                        <a href={artist.tipLinks.paypal} target="_blank" rel="noopener noreferrer">
                          <Button className="bg-[#003087] hover:bg-[#003087]/90 text-white" size="sm">
                            PayPal
                          </Button>
                        </a>
                      )}
                      {artist.tipLinks.venmo && (
                        <a href={artist.tipLinks.venmo} target="_blank" rel="noopener noreferrer">
                          <Button className="bg-[#3D95CE] hover:bg-[#3D95CE]/90 text-white" size="sm">
                            Venmo
                          </Button>
                        </a>
                      )}
                      {artist.tipLinks.cashApp && (
                        <a href={artist.tipLinks.cashApp} target="_blank" rel="noopener noreferrer">
                          <Button className="bg-[#00D632] hover:bg-[#00D632]/90 text-white" size="sm">
                            Cash App
                          </Button>
                        </a>
                      )}
                      {artist.tipLinks.customTip && (
                        <a href={artist.tipLinks.customTip} target="_blank" rel="noopener noreferrer">
                          <Button className="bg-purple-600 hover:bg-purple-600/90 text-white" size="sm">
                            More
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Upcoming Events */}
              {upcomingEvents.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Upcoming Events
                  </h2>
                  <div className="space-y-3">
                    {upcomingEvents.map(event => (
                      <Link key={event.id} to={`/events/${event.id}`}>
                        <Card className="bg-primary/10 border-primary/30 p-4 hover:border-primary/50 transition-colors mb-3">
                          <div className="flex items-center gap-4">
                            <img 
                              src={event.image} 
                              alt={event.title}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div className="flex-1">
                              <h3 className="font-semibold text-white">{event.title}</h3>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(event.date)}
                              </p>
                            </div>
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary/20 text-primary">
                              Upcoming
                            </span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Past Events */}
              {pastEvents.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-white mb-4">Past Events</h2>
                  <div className="space-y-3">
                    {pastEvents.map(event => (
                      <Link key={event.id} to={`/events/${event.id}`}>
                        <Card className="bg-secondary/50 border-border p-4 hover:border-primary/50 transition-colors mb-3">
                          <div className="flex items-center gap-4">
                            <img 
                              src={event.image} 
                              alt={event.title}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div className="flex-1">
                              <h3 className="font-semibold text-white">{event.title}</h3>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(event.date)}
                              </p>
                            </div>
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-muted text-muted-foreground">
                              Past
                            </span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

export default UserArtistDetails;
